import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cogoToast from "cogo-toast";
import ProfileLayout from "../../components/profile-redesign/ProfileLayout";
import Panel from "../../components/profile-redesign/Panel";
import Notice from "../../components/profile-redesign/Notice";
import { useProfile } from "../../components/profile-redesign/useProfile";
import { useUpdateUserProfileMutation } from "../../store/slices/user-slice";
import { initials } from "../../components/profile-redesign/profileData";

/* Exactly the fields PUT /user/update/:id writes. */
const FIELDS = [
  { name: "name", label: "Full Name", required: true, autoComplete: "name" },
  { name: "phoneNumber", label: "Phone Number", type: "tel", autoComplete: "tel" },
  { name: "city", label: "City", autoComplete: "address-level2" },
  { name: "state", label: "State / Province", autoComplete: "address-level1" },
  { name: "country", label: "Country", autoComplete: "country-name" },
];

const emptyForm = { name: "", phoneNumber: "", city: "", state: "", country: "" };

function ProfileEdit() {
  const navigate = useNavigate();
  const { user, isLoading, isError, signOut } = useProfile();
  const [updateUserProfile, { isLoading: isSaving }] =
    useUpdateUserProfileMutation();

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      phoneNumber: user.phoneNumber || "",
      city: user.city || "",
      state: user.state || "",
      country: user.country || "",
    });
  }, [user]);

  const change = (name) => (e) =>
    setForm((current) => ({ ...current, [name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError("Your name can't be empty.");
      return;
    }

    setError("");
    try {
      await updateUserProfile({
        userId: user._id,
        updatedData: form,
      }).unwrap();
      cogoToast.success("Profile updated.");
      navigate(process.env.PUBLIC_URL + "/profile");
    } catch (err) {
      console.error("Update failed:", err);
      setError(
        err?.data?.message || "We couldn't save those changes. Please try again."
      );
    }
  };

  return (
    <ProfileLayout
      seoTitle="Edit Profile"
      title="Edit Profile"
      subtitle="Update your personal information and preferences."
      user={user}
      isLoading={isLoading}
      isError={isError}
      onSignOut={signOut}
    >
      <form onSubmit={handleSubmit} noValidate>
        <Panel id="c4prf-photo" title="Profile Photo">
          <div className="c4prf__photo">
            <span className="c4prf__photo-tile" aria-hidden="true">
              {initials(user?.name || user?.email)}
            </span>
            <div>
              <p className="c4prf__photo-name">{user?.name}</p>
              <p className="c4prf__photo-hint">
                Your initials stand in for a picture. The account record has no
                photo field, so uploads aren't stored yet.
              </p>
            </div>
          </div>
        </Panel>

        <Panel id="c4prf-personal" title="Personal Information">
          <div className="c4prf__fields">
            {FIELDS.map((field) => (
              <div className="c4prf__field" key={field.name}>
                <label className="c4prf__label" htmlFor={`c4prf-${field.name}`}>
                  {field.label}
                  {field.required ? <span aria-hidden="true"> *</span> : null}
                </label>
                <input
                  className="c4prf__input"
                  id={`c4prf-${field.name}`}
                  name={field.name}
                  type={field.type || "text"}
                  autoComplete={field.autoComplete}
                  value={form[field.name]}
                  onChange={change(field.name)}
                  required={field.required}
                />
              </div>
            ))}

            <div className="c4prf__field">
              <label className="c4prf__label" htmlFor="c4prf-email">
                Email Address
              </label>
              <input
                className="c4prf__input"
                id="c4prf-email"
                type="email"
                value={user?.email || ""}
                readOnly
                aria-describedby="c4prf-email-note"
              />
              <p className="c4prf__hint" id="c4prf-email-note">
                {user?.isVerified
                  ? "Verified. Email changes aren't handled from this page."
                  : "Not yet verified. Check your inbox for the confirmation link."}
              </p>
            </div>
          </div>
        </Panel>

        {error ? (
          <p className="c4prf__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="c4prf__form-actions">
          <button
            className="c4prf__btn c4prf__btn--primary"
            type="submit"
            disabled={isSaving || !user}
          >
            {isSaving ? "Saving…" : "Save Changes"}
          </button>
          <button
            className="c4prf__btn c4prf__btn--outline"
            type="button"
            onClick={() => navigate(process.env.PUBLIC_URL + "/profile")}
          >
            Cancel
          </button>
        </div>

        <Notice>
          Date of birth, gender, display name and language preference aren't
          shown because the account record has no fields for them — saving
          would silently discard whatever you typed.
        </Notice>
      </form>
    </ProfileLayout>
  );
}

export default ProfileEdit;
